import React from 'react';
import { Box, ParagraphText } from '@leagueplatform/genesis-core';

// Category tokens emitted by the masonry-scfhp BE adapter; mirrors the
// composableCard / heroBanner tag.category enum.
export type FormStatusCategory =
  | 'informational'
  | 'warning'
  | 'success'
  | 'critical'
  | 'neutral';

interface FormStatusPillProps {
  label: string;
  category: FormStatusCategory;
  // Status definitions colour the label with the tag text token; the detail
  // header keeps the default dark label per Figma.
  labelTone?: 'default' | 'category';
}

const pillBackgroundColor: Record<FormStatusCategory, string> = {
  informational: 'var(--GDS-colors-tagInformationalBackgroundSubdued)',
  warning: 'var(--GDS-colors-tagWarningBackgroundSubdued)',
  success: 'var(--GDS-colors-tagSuccessBackgroundSubdued)',
  critical: 'var(--GDS-colors-tagCriticalBackgroundSubdued)',
  neutral: 'var(--GDS-colors-tagNeutralBackgroundSubdued)',
};

// Genesis exposes no neutral status icon token, so neutral keeps the tag text colour.
const pillDotColor: Record<FormStatusCategory, string> = {
  informational: 'var(--GDS-colors-highlightIcon)',
  warning: 'var(--GDS-colors-warningIcon)',
  success: 'var(--GDS-colors-successIcon)',
  critical: 'var(--GDS-colors-criticalIcon)',
  neutral: 'var(--GDS-colors-tagNeutralTextSubdued)',
};

const pillLabelColor: Record<FormStatusCategory, string> = {
  informational: 'var(--GDS-colors-tagInformationalTextSubdued)',
  warning: 'var(--GDS-colors-tagWarningTextSubdued)',
  success: 'var(--GDS-colors-tagSuccessTextSubdued)',
  critical: 'var(--GDS-colors-tagCriticalTextSubdued)',
  neutral: 'var(--GDS-colors-tagNeutralTextSubdued)',
};

export const FormStatusPill: React.FC<FormStatusPillProps> = ({
  label,
  category,
  labelTone = 'default',
}) => {
  const backgroundColor =
    pillBackgroundColor[category] ?? pillBackgroundColor.neutral;
  const dotColor = pillDotColor[category] ?? pillDotColor.neutral;
  const labelColor =
    labelTone === 'category'
      ? pillLabelColor[category] ?? pillLabelColor.neutral
      : '$onSurfaceTextPrimary';

  return (
    <Box
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '24px',
        padding: '0 10px',
        gap: '6px',
        borderRadius: '99999px',
        backgroundColor,
      }}
    >
      <Box
        css={{
          width: '8px',
          height: '8px',
          backgroundColor: dotColor,
          borderRadius: '50%',
          flexShrink: 0,
        }}
      />
      <ParagraphText
        css={{
          typography: '$bodyTwo',
          fontWeight: 600,
          color: labelColor,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </ParagraphText>
    </Box>
  );
};

export const bandBackgroundColor = pillBackgroundColor;
